import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const HeroSection = styled.section`
  min-height: 90vh;
  background: linear-gradient(135deg, #148D89 0%, #1C64F2 100%);
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
    animation: pulse 15s ease-in-out infinite;
  }
  
  @keyframes pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.1); }
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const HeroTitle = styled.h1`
  color: white;
  font-size: clamp(2.5rem, 5vw, 4rem);
  margin-bottom: 1.5rem;
  animation: fadeInUp 1s ease-out;
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const HeroSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: clamp(1.2rem, 3vw, 1.5rem);
  margin-bottom: 2rem;
  max-width: 600px;
  animation: fadeInUp 1s ease-out 0.2s both;
`;

const HeroButtons = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  animation: fadeInUp 1s ease-out 0.4s both;
`;


const SecondaryButton = styled(Link)`
  background: transparent;
  color: white;
  padding: 1rem 2rem;
  border: 2px solid white;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  display: inline-block;
  
  &:hover {
    background: white;
    color: #148D89;
  }
`;

const FeaturesSection = styled.section`
  padding: 5rem 2rem;
  background: white;
`;

const SectionTitle = styled.h2`
  text-align: center;
  color: #15243A;
  margin-bottom: 3rem;
`;

const FeaturesGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
`;

const FeatureCard = styled.div`
  padding: 2rem;
  border-radius: 15px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
  }
  
  h3 {
    color: #148D89;
    margin-bottom: 1rem;
  }
  
  p {
    color: #4B5563;
  }
`;

const CoursesSection = styled.section`
  padding: 5rem 2rem;
  background: #F9FAFB;
`;

const CoursesGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
`;

const CourseCard = styled.div`
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
  }
`;

const CourseHeader = styled.div`
  background: linear-gradient(135deg, #148D89 0%, #1C64F2 100%);
  padding: 2rem;
  color: white;
  
  h3 {
    margin-bottom: 0.5rem;
  }
`;

const CourseContent = styled.div`
  padding: 2rem;
  
  p {
    color: #6B7280;
    margin-bottom: 1rem;
  }
  
  .price {
    font-size: 1.5rem;
    font-weight: 700;
    color: #148D89;
    margin: 1rem 0;
  }
`;

const CourseButton = styled(Link)`
  display: inline-block;
  background: #148D89;
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 50px;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    background: #1C64F2;
  }
`;

const StatsSection = styled.section`
  padding: 4rem 2rem;
  background: linear-gradient(135deg, #148D89 0%, #1C64F2 100%);
  color: white;
`;

const StatsGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  text-align: center;
`;

const StatItem = styled.div`
  h3 {
    font-size: 2.5rem;
    margin-bottom: 0.5rem;
  }
  
  p {
    opacity: 0.9;
  }
`;

const Home: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Institut Genevois de Formation - Formations Médicales en Ligne</title>
        <meta name="description" content="Formations professionnelles 100% en ligne pour devenir Secrétaire Médical ou Assistant Médical. Institut Genevois de Formation." />
        <meta name="keywords" content="formation médicale, secrétaire médicale, assistant médical, formation en ligne, certification, Genève, Saint-Julien-en-Genevois" />
      </Helmet>
      
      <HeroSection>
        <HeroContent>
          <HeroTitle>Votre avenir dans le médical commence ici</HeroTitle>
          <HeroSubtitle>
            Formations professionnelles 100% en ligne pour devenir Secrétaire Médicale ou Assistant Médical
          </HeroSubtitle>
          <HeroButtons>
            <SecondaryButton to="#formations">Découvrir nos formations</SecondaryButton>
          </HeroButtons>
        </HeroContent>
      </HeroSection>
      
      <FeaturesSection>
        <SectionTitle>Pourquoi choisir l'Institut Genevois de Formation ?</SectionTitle>
        <FeaturesGrid>
          <FeatureCard>
            <h3>🎓 100% en ligne</h3>
            <p>Formez-vous à votre rythme, où que vous soyez, avec nos formations entièrement digitalisées</p>
          </FeatureCard>
          <FeatureCard>
            <h3>✅ Certifications reconnues</h3>
            <p>Obtenez un diplôme certifié IGF reconnu par les professionnels du secteur médical</p>
          </FeatureCard>
          <FeatureCard>
            <h3>👥 Accompagnement personnalisé</h3>
            <p>Sessions en visio, corrections individuelles et suivi personnalisé tout au long de votre parcours</p>
          </FeatureCard>
          <FeatureCard>
            <h3>💼 Pratique professionnelle</h3>
            <p>Exercices pratiques, mises en situation et cas réels pour une préparation optimale</p>
          </FeatureCard>
          <FeatureCard>
            <h3>🚀 Insertion professionnelle</h3>
            <p>Accompagnement dans votre recherche d'emploi et mise en relation avec notre réseau de partenaires</p>
          </FeatureCard>
          <FeatureCard>
            <h3>💰 Financement flexible</h3>
            <p>Solutions de financement adaptées à votre situation avec possibilité de paiement échelonné</p>
          </FeatureCard>
        </FeaturesGrid>
      </FeaturesSection>
      
      <CoursesSection id="formations">
        <SectionTitle>Nos Formations</SectionTitle>
        <CoursesGrid>
          <CourseCard>
            <CourseHeader>
              <h3>Secrétaire Médicale</h3>
              <p>Formation complète pour la gestion administrative médicale</p>
            </CourseHeader>
            <CourseContent>
              <p>Apprenez la gestion administrative, l'accueil des patients et les outils informatiques spécialisés.</p>
              <ul>
                <li>✓ Accueil et communication</li>
                <li>✓ Gestion administrative</li>
                <li>✓ Outils informatiques médicaux</li>
                <li>✓ Facturation et télétransmission</li>
              </ul>
              <p className="price">1400€</p>
              <CourseButton to="/secretaire-medical">En savoir plus →</CourseButton>
            </CourseContent>
          </CourseCard>
          
          <CourseCard>
            <CourseHeader>
              <h3>Assistant Médical</h3>
              <p>Accompagnez les professionnels de santé au quotidien</p>
            </CourseHeader>
            <CourseContent>
              <p>Formation pratique et théorique complète pour assister efficacement les praticiens.</p>
              <ul>
                <li>✓ Assistance médicale</li>
                <li>✓ Prise des constantes</li>
                <li>✓ Installation des patients</li>
                <li>✓ Organisation du cabinet</li>
              </ul>
              <p className="price">2000€</p>
              <CourseButton to="/assistant-medical">En savoir plus →</CourseButton>
            </CourseContent>
          </CourseCard>
        </CoursesGrid>
      </CoursesSection>
      
      <StatsSection>
        <StatsGrid>
          <StatItem>
            <h3>100%</h3>
            <p>Formation en ligne</p>
          </StatItem>
          <StatItem>
            <h3>3-6</h3>
            <p>Mois de formation</p>
          </StatItem>
          <StatItem>
            <h3>95%</h3>
            <p>Taux de réussite</p>
          </StatItem>
          <StatItem>
            <h3>500+</h3>
            <p>Élèves formés</p>
          </StatItem>
        </StatsGrid>
      </StatsSection>
    </>
  );
};

export default Home;